import { UniqueIdentifier } from '@dnd-kit/core';
import { QueueCard } from './QueueCard';
import { SortableItem, DroppableContainer, Items, KanbanDndPropsChilrenProps } from '../dndComponents';
import { KanbanIntersection } from './KanbanIntersection';
import { parseColumnId } from '../utils';
import { KanbanColumnHeader } from './KanbanColumnHeader';
import { useCustomTranslation } from '../../../../../hooks';
import { SUPPORTED_KANBAN_STATUSES } from '../SupportedKanbanStatuses';
import { useKanbanEventStore } from '../store';
import { Skeleton } from '@nexelem/components';
import { KanbanTaskResolver } from './KanbanTaskResolver';
import { useDisableKanbanDnd } from '../dndComponents/useDisableKanbanDnd';
import { LocateProductionTask } from '../../Gantt/hooks';
import { KanbanFilterEmptyState } from './KanbanFilterEmptyState';

const PER_CHUNK = 3; // items per chunk

type Column = {
  taskIds: UniqueIdentifier[];
  columnId: string;
  queueId: number;
};

type Queue = {
  queueId: number;
  maxElements: number;
  columns: Column[];
};

const groupItemsToQueues = (items: Items): Queue[] =>
  Object.entries(items)
    .map(([columnId, taskIds]) => {
      const { queueId } = parseColumnId(columnId);
      return {
        columnId,
        taskIds,
        queueId: Number(queueId),
      };
    })
    .reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / PER_CHUNK);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex]?.push(item);

      return resultArray;
    }, [] as Column[][])
    .map((columns) => {
      return {
        queueId: columns[0]?.queueId || 0,
        maxElements: columns.reduce((acc, { taskIds }) => (acc > taskIds.length ? acc : taskIds.length), 0),
        columns,
      };
    });

export const KanbanRenderer = ({ items, isSortingContainer }: KanbanDndPropsChilrenProps) => {
  const disableKanbanDnd = useDisableKanbanDnd();
  const queues = groupItemsToQueues(items);
  const { tWrapper } = useCustomTranslation('products.productionPlan.kanban');
  const isLoading = useKanbanEventStore((state) => state.isLoading);

  if (!isLoading && queues.length === 0) {
    return <KanbanFilterEmptyState />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="grid shrink-0 grid-cols-14 border-b border-border-primary">
        <div className="col-span-2 py-3 text-center text-s16 text-text-secondary">{tWrapper('queue')}</div>
        {SUPPORTED_KANBAN_STATUSES.map((status) => (
          <KanbanColumnHeader className="col-span-4" key={status} status={status} />
        ))}
      </div>
      <div
        className="min-h-0 flex-1 overflow-auto border-border-primary bg-surface-container"
        id={LocateProductionTask.scrollContainerId}
      >
        <div className="mb-6 flex flex-col">
          {isLoading && (
            <div className="mt-6 grid grid-cols-14 bg-surface-primary">
              <QueueCard queueId={0} />
              {SUPPORTED_KANBAN_STATUSES.map((_, index) => {
                const COLUMN_SKELETON_COUNTS = [6, 3, 4];
                return (
                  <div className="col-span-4 border border-l-0 border-border-primary p-6" key={index}>
                    <div className="flex flex-col gap-4">
                      {Array.from({ length: COLUMN_SKELETON_COUNTS[index] ?? 3 }).map((_, i) => (
                        <Skeleton className="h-38" key={i} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!isLoading &&
            queues.map(({ queueId, columns, maxElements }) => (
              <KanbanIntersection
                className="mt-6 mb-6 grid grid-cols-14 bg-surface-primary"
                elements={maxElements}
                key={queueId}
                queueId={queueId}
              >
                <QueueCard queueId={queueId} />
                {columns.map(({ taskIds, columnId }) => (
                  <div
                    className="relative col-span-4 border border-l-0 border-border-primary p-6 has-[.disabled]:bg-surface-disabled"
                    key={columnId}
                  >
                    <DroppableContainer
                      className="flex h-full min-h-24 flex-col gap-4"
                      columnId={columnId}
                      items={taskIds}
                      key={columnId}
                    >
                      {taskIds.length > 0 &&
                        taskIds.map((taskId, index) => (
                          <SortableItem
                            columnId={columnId}
                            disabled={disableKanbanDnd || isSortingContainer}
                            id={taskId}
                            index={index}
                            key={taskId}
                          >
                            <KanbanTaskResolver id={taskId as number} />
                          </SortableItem>
                        ))}
                    </DroppableContainer>
                  </div>
                ))}
              </KanbanIntersection>
            ))}
        </div>
      </div>
    </div>
  );
};
